import { createClient } from '@supabase/supabase-js';

import type { UserRole } from '@/lib/roles';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

// Regular client for normal operations
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// User management (create logins, reset passwords) runs server-side in
// api/admin-users.ts - the service role key must never reach the browser.

// Auth helpers
export const signIn = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  return { data, error };
};

// Microsoft / Entra ID sign-in. Supabase brokers the OIDC flow, so the client
// secret stays in the Supabase dashboard and never reaches the browser.
//
// loginHint must be a full address to be useful. Entra prefills it and skips the
// account picker, which matters on shared station PCs where a personal Microsoft
// account may already be signed in.
export const signInWithMicrosoft = async (loginHint?: string) => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'azure',
    options: {
      scopes: 'openid profile email',
      redirectTo: window.location.origin,
      ...(loginHint ? { queryParams: { login_hint: loginHint } } : {}),
    },
  });
  return { data, error };
};

export const signUp = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signUp({ email, password });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// getUserRole now uses the get_user_role() DB function preserved from old schema.
// Falls back to 'admin' if the function is unavailable.
export const getUserRole = async (userId: string): Promise<UserRole> => {
  try {
    const { data, error } = await supabase.rpc('get_user_role');
    
    // If RPC fails, try checking the technicians table directly as a fallback
    if (error || !data) {
      const { data: techData } = await supabase
        .from('technicians')
        .select('role')
        .eq('user_id', userId)
        .single();
        
      return (techData?.role as UserRole) || 'technician';
    }

    // Normalize 'management' to 'manager' if DB returns old string
    if (data === 'management') return 'manager';
    
    return data as UserRole;
  } catch (e) {
    return 'technician'; // Safest default
  }
};
